import { useEffect, useRef } from 'react';

import type {
  DeviceMetricsViewModel,
  HubDeviceDrawerViewModel
} from '../deviceHealthProjection';

export type HubDeviceDrawerProps = {
  isOpen: boolean;
  view: 'hub' | 'device';
  hub: HubDeviceDrawerViewModel | null;
  selectedDeviceID: string | null;
  metrics: DeviceMetricsViewModel | null;
  loading: boolean;
  error: string | null;
  grafanaAvailable: boolean;
  onClose: () => void;
  onSelectDevice: (deviceID: string) => void;
  onBack: () => void;
  onRetry: () => void;
  onOpenGrafana: (target: { deviceId: string }) => void;
};

const stateTone = (state: string) => {
  switch (state) {
    case 'healthy':
    case 'available':
    case 'reachable':
    case 'fresh':
    case 'up':
      return 'text-[color:var(--color-success)]';
    case 'degraded':
    case 'stale':
      return 'text-[color:var(--color-warning)]';
    case 'critical':
    case 'failed':
    case 'unreachable':
    case 'down':
      return 'text-[color:var(--color-error)]';
    default:
      return 'text-muted-foreground';
  }
};

const label = (value: string) =>
  value.replace(/_/g, ' ').replace(/\b\w/g, character => character.toUpperCase());

const metricValue = (metric: DeviceMetricsViewModel['groups'][number]['metrics'][number]) => {
  if (metric.availability !== 'available') return label(metric.availability);
  const value = metric.value ?? metric.state;
  if (value === undefined) return 'Unavailable';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return `${value}${metric.unit ? ` ${metric.unit}` : ''}`;
};

const DrawerBody = ({
  view,
  hub,
  selectedDeviceID,
  metrics,
  loading,
  error,
  grafanaAvailable,
  onClose,
  onSelectDevice,
  onBack,
  onRetry,
  onOpenGrafana
}: HubDeviceDrawerProps) => (
  <div className="flex h-full min-h-0 flex-col border-l border-border/60 bg-background/95 text-foreground backdrop-blur">
    <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border/50 px-4 py-3">
      <div className="min-w-0">
        {view === 'device' ? (
          <button
            type="button"
            onClick={onBack}
            className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--color-info)] hover:underline"
          >
            Back to {hub?.hubName ?? 'hub'}
          </button>
        ) : null}
        <div className="truncate text-sm font-semibold">
          {view === 'device'
            ? metrics?.displayName ?? hub?.devices.find(device => device.id === selectedDeviceID)?.displayName ?? 'Device metrics'
            : hub?.hubName ?? 'Hub devices'}
        </div>
        <div className="mt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
          {view === 'device' ? 'Device telemetry' : 'Device health'}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close device details"
        className="rounded border border-border/50 px-2 py-1 text-xs text-muted-foreground transition hover:bg-accent hover:text-foreground"
      >
        Close
      </button>
    </div>

    <div className="min-h-0 flex-1 overflow-y-auto p-4">
      {view === 'hub' && hub ? (
        <div className="space-y-4" data-testid="hub-device-list">
          <section className="rounded-lg border border-border/40 bg-card/25 p-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Overall health
              </span>
              <span className={`text-xs font-semibold ${stateTone(hub.overall)}`}>
                {label(hub.overall)}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-3">
              <span>Monitored {hub.summary.monitored_count}</span>
              <span className={stateTone('healthy')}>Healthy {hub.summary.healthy_count}</span>
              <span className={stateTone('degraded')}>Degraded {hub.summary.degraded_count}</span>
              <span className={stateTone('critical')}>Critical {hub.summary.critical_count}</span>
              <span className={stateTone('unknown')}>Unknown {hub.summary.unknown_count}</span>
              <span>Freshness {label(hub.summary.worst_freshness)}</span>
            </div>
          </section>

          <section>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Devices {hub.devices.length}
            </div>
            <div className="space-y-2">
              {hub.devices.map(device => (
                <button
                  key={device.id}
                  type="button"
                  onClick={() => onSelectDevice(device.id)}
                  className="block w-full rounded-lg border border-border/40 bg-card/25 p-3 text-left transition hover:border-[color:var(--color-info)]/60 hover:bg-accent/50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-xs font-semibold">{device.displayName}</div>
                      <div className="truncate text-[10px] text-muted-foreground">
                        {[device.hostname, device.role].filter(Boolean).join(' · ') || 'Network device'}
                      </div>
                    </div>
                    <span className={`text-[10px] font-semibold ${stateTone(device.reachability)}`}>
                      {label(device.reachability)}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-1 text-[10px] text-muted-foreground">
                    <span>Collection {label(device.collectionHealth)}</span>
                    <span>Freshness {label(device.freshness)}</span>
                    <span>Overlays {device.overlayReady}/{device.overlayExpected}</span>
                    <span>Alerts {device.alertCount}</span>
                  </div>
                </button>
              ))}
              {!hub.devices.length ? (
                <div className="rounded border border-border/30 p-3 text-xs text-muted-foreground">
                  No device details are present in the current map projection.
                </div>
              ) : null}
            </div>
          </section>

          <section>
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Active alerts {hub.summary.confirmed_alert_count}
            </div>
            <div className="space-y-2">
              {hub.alerts.map(alert => (
                <div key={alert.alert_id} className="rounded border border-border/40 bg-card/25 p-3">
                  <div className={`text-[10px] font-semibold uppercase ${stateTone(alert.severity)}`}>
                    {alert.severity}
                  </div>
                  <div className="mt-1 text-xs">{alert.summary}</div>
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    {alert.resource_kind}{alert.acknowledged ? ' · Acknowledged' : ''}
                  </div>
                </div>
              ))}
              {!hub.alerts.length ? (
                <div className="text-xs text-muted-foreground">No confirmed active alerts.</div>
              ) : null}
              {hub.alerts.length < hub.summary.confirmed_alert_count ? (
                <div className="text-[10px] text-muted-foreground">
                  Showing {hub.alerts.length} of {hub.summary.confirmed_alert_count} confirmed alerts.
                </div>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {view === 'device' ? (
        <div className="space-y-3" data-testid="device-metrics-detail">
          {loading ? <div className="text-xs text-muted-foreground">Loading device metrics…</div> : null}
          {error ? (
            <div className="rounded border border-[color:var(--color-error)]/40 bg-[color:var(--color-error)]/10 p-3 text-xs">
              <div>{error}</div>
              <button type="button" onClick={onRetry} className="mt-2 font-semibold underline">
                Retry
              </button>
            </div>
          ) : null}
          {metrics ? (
            <>
              <section className="rounded-lg border border-border/40 bg-card/25 p-3 text-[11px]">
                <div className="grid grid-cols-2 gap-2">
                  <span>Reachability</span>
                  <span className={stateTone(metrics.reachability)}>{label(metrics.reachability)}</span>
                  <span>Collection</span>
                  <span className={stateTone(metrics.collectionHealth)}>{label(metrics.collectionHealth)}</span>
                  <span>Freshness</span>
                  <span className={stateTone(metrics.freshness)}>{label(metrics.freshness)}</span>
                  <span>Alerts</span>
                  <span>{metrics.alertCount}{metrics.alertsTruncated ? '+' : ''}</span>
                </div>
              </section>
              {metrics.groups.map(group => (
                <section key={group.category} className="rounded-lg border border-border/40 bg-card/25 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em]">
                      {group.label}
                    </div>
                    <span className={`text-[10px] font-semibold ${stateTone(group.availability)}`}>
                      {label(group.availability)}
                    </span>
                  </div>
                  {group.diagnostic ? (
                    <div className="mt-2 text-[10px] text-muted-foreground">{group.diagnostic}</div>
                  ) : null}
                  <div className="mt-2 divide-y divide-border/30">
                    {group.metrics.map(metric => (
                      <div key={`${metric.key}-${metric.component_id ?? metric.interface_id ?? ''}`} className="py-2">
                        <div className="flex items-start justify-between gap-3 text-[11px]">
                          <span>{metric.label}</span>
                          <span className={`text-right font-medium ${stateTone(metric.availability)}`}>
                            {metricValue(metric)}
                          </span>
                        </div>
                        {metric.component_name || metric.component_kind || metric.observed_at || metric.diagnostic ? (
                          <div className="mt-1 text-[9px] text-muted-foreground">
                            {[metric.component_name, metric.component_kind, metric.observed_at, metric.diagnostic]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        ) : null}
                      </div>
                    ))}
                    {!group.metrics.length ? (
                      <div className="py-2 text-[10px] text-muted-foreground">
                        No current metrics in this group.
                      </div>
                    ) : null}
                  </div>
                  {group.truncated ? (
                    <div className="mt-2 text-[9px] text-muted-foreground">
                      Showing {group.returnedCount} of {group.totalCount}.
                    </div>
                  ) : null}
                </section>
              ))}
              {grafanaAvailable ? (
                <button
                  type="button"
                  onClick={() => onOpenGrafana({ deviceId: metrics.deviceID })}
                  className="w-full rounded-md border border-[color:var(--color-info)]/50 px-3 py-2 text-xs font-semibold text-[color:var(--color-info)] transition hover:bg-accent"
                >
                  Open longer history in Grafana
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  </div>
);

export const HubDeviceDrawer = (props: HubDeviceDrawerProps) => {
  const desktopRef = useRef<HTMLDivElement | null>(null);
  const mobileRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!props.isOpen) return;
    const isDesktop =
      typeof window.matchMedia !== 'function' ||
      window.matchMedia('(min-width: 768px)').matches;
    (isDesktop ? desktopRef.current : mobileRef.current)?.focus();
  }, [props.isOpen, props.view]);
  useEffect(() => {
    if (!props.isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [props.isOpen, props.onClose]);
  if (!props.isOpen) return null;
  return (
    <>
      <div
        ref={mobileRef}
        className="pointer-events-auto absolute inset-0 z-30 md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Hub device details"
        tabIndex={-1}
      >
        <button
          type="button"
          aria-label="Close hub device details"
          className="absolute inset-0 bg-background/65 backdrop-blur-[2px]"
          onClick={props.onClose}
        />
        <div className="absolute right-0 top-0 h-full w-full shadow-2xl">
          <DrawerBody {...props} />
        </div>
      </div>
      <div
        ref={desktopRef}
        className="pointer-events-auto relative z-30 hidden h-full w-[min(50%,760px)] shrink-0 shadow-2xl outline-none md:block"
        role="dialog"
        aria-modal="false"
        aria-label="Hub device details"
        tabIndex={-1}
      >
        <DrawerBody {...props} />
      </div>
    </>
  );
};
